import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import FeedIcon from '@mui/icons-material/Feed';
import MenuIcon from '@mui/icons-material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import CustomAppBar from './components/CustomAppBar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'providers/AuthContextProvider';
import { Avatar, Link, Stack } from '@mui/material';
import capitalizeFirstLetter from 'utils/capitalizeFirstLetter';

const drawerWidth = 350;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export default function Main({props}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate()
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const footerAction = (action)=>{
    if(action == 'logout'){
      logout()
    }else{

    }
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/*<CustomAppBar handleDrawerOpen={handleDrawerOpen} setOpen={setOpen} open={open}></CustomAppBar>*/}

      <Drawer variant="permanent" open={open}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            p: 2,
          }}
        >
          <Stack
            direction={open ? 'column' : 'row'}
            alignItems="left"
            spacing={open ? 1 : 0}
          >
            {open && (
              <Stack spacing={0.3} alignItems="left">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                  <img
                    src="https://firebasestorage.googleapis.com/v0/b/xpediente-clinico.appspot.com/o/logos%2Fcardex_medico_logo.png?alt=media&token=0ba70358-0587-41a6-a5cf-367f17fe8ac4"
                    alt="Cardex Médico"
                    style={{ width: 128, height: 64, cursor: 'pointer' }}
                  />
                </Link>
              </Stack>
            )}
          </Stack>
        </Box>
        <Divider />
        <List>
          {[{title:'Home', route:'/sons', icon:<HomeIcon />  }, {title:'Profile', route:'/profile', icon:<PersonIcon /> },{title:'xpediente', route:'/expediente', icon:<FeedIcon /> }].map((item, index) => (
            <ListItem key={item.title} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={()=>{ navigate(item.route)}}
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                  },
                  open
                    ? {
                        justifyContent: 'initial',
                      }
                    : {
                        justifyContent: 'center',
                      },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: 'center',
                    },
                    open
                      ? {
                          mr: 3,
                        }
                      : {
                          mr: 'auto',
                        },
                  ]}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  sx={[
                    open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                        },
                  ]}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {['All mail'].map((text, index) => (
            <ListItem key={text} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={[
                  {
                    minHeight: 48,
                    px: 2.5,
                  },
                  open
                    ? {
                        justifyContent: 'initial',
                      }
                    : {
                        justifyContent: 'center',
                      },
                ]}
              >
                <ListItemIcon
                  sx={[
                    {
                      minWidth: 0,
                      justifyContent: 'center',
                    },
                    open
                      ? {
                          mr: 3,
                        }
                      : {
                          mr: 'auto',
                        },
                  ]}
                >
                  {index % 2 === 0 ? <InboxIcon /> : <MailIcon />}
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  sx={[
                    open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                        },
                  ]}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {/* FOOTER */}
        <Box
          sx={{
            mt: "auto",
            py:2,
            textAlign: open ? "left" : "center",
            opacity: 0.7,
          }}
        >
          {open ? (
            <Stack>
              <List>
                {[{title:'Log out', route:'logout', icon:<LogoutIcon/> }, {title:'Más', route:'mas', icon: <MenuIcon/> }].map((item, index) => (
                  <ListItem key={item.title} disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={()=>{ footerAction(item.route)}}
                      sx={[
                        {
                          minHeight: 48,
                          px: 2.5,
                        },
                        open
                          ? {
                              justifyContent: 'initial',
                            }
                          : {
                              justifyContent: 'center',
                            },
                      ]}
                    >
                      <ListItemIcon
                        sx={[
                          {
                            minWidth: 0,
                            justifyContent: 'center',
                          },
                          open
                            ? {
                                mr: 3,
                              }
                            : {
                                mr: 'auto',
                              },
                        ]}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        sx={[
                          open
                          ? {
                              opacity: 1,
                            }
                          : {
                              opacity: 0,
                            },
                        ]}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                <Divider />
                <ListItem key={"version"} disablePadding sx={{ display: 'block', p: 2.5, }}>
                  <ListItemText
                    primary={" © Cardex Médico v1.0"}
                    sx={[
                      open
                      ? {
                          opacity: 1,
                        }
                      : {
                          opacity: 0,
                        },
                    ]}
                  />
                </ListItem>
              </List>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              © 25
            </Typography>
          )}
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, px: 3, py:0}}>   
        {props}
      </Box>
    </Box>
  );
}